import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Select, Space, Spin, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';

import useRequest from '../hooks/useRequest';
import { type BusinessTimeZoneView, toTimeZoneOptions } from '../domain/businessTimeZone';

const { Title, Paragraph, Text } = Typography;

/**
 * Configuration > Time zone (administrators): the shop's time zone, which every date and time
 * the backend records, compares or schedules by follows - both programs' operations, the nightly
 * status refresh and statistics, the cron jobs and MySQL's own clock. Applied on save, no restart.
 */
const BusinessTimeZoneSettings = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const [view, setView] = useState<BusinessTimeZoneView | null>(null);
    const [selected, setSelected] = useState<string | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        request<{ data: BusinessTimeZoneView }>({ path: '/configuration/time-zone' })
            .then(response => {
                if (!cancelled) {
                    setView(response.data);
                    setSelected(response.data.timeZone);
                }
            })
            .catch(() => {
                if (!cancelled) setLoadFailed(true);
            });
        return () => {
            cancelled = true;
        };
    }, [request]);

    const options = useMemo(() => toTimeZoneOptions(view?.options ?? [], new Date()), [view]);

    const save = async () => {
        if (selected === null) return;
        setSaving(true);
        try {
            const response = await request<{ data: BusinessTimeZoneView }>({
                path: '/configuration/time-zone',
                method: 'PUT',
                params: { timeZone: selected },
            });
            setView(response.data);
            setSelected(response.data.timeZone);
            message.success(t('configuration.timeZone.saved'));
        } catch {
            message.error(t('configuration.timeZone.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    if (loadFailed) {
        return <Alert type="error" showIcon message={t('configuration.timeZone.loadFailed')} />;
    }
    if (view === null) {
        return <Spin />;
    }

    return (
        <div data-testid="business-time-zone-settings">
            <Title level={4}>{t('configuration.timeZone.title')}</Title>
            <Paragraph type="secondary">{t('configuration.timeZone.description')}</Paragraph>
            <Space wrap align="center">
                <Text strong>{t('configuration.timeZone.label')}</Text>
                <Select
                    data-testid="business-time-zone-select"
                    aria-label={t('configuration.timeZone.label')}
                    showSearch
                    optionFilterProp="label"
                    style={{ minWidth: 320 }}
                    value={selected}
                    options={options}
                    onChange={setSelected}
                />
                <Button
                    type="primary"
                    data-testid="business-time-zone-save"
                    loading={saving}
                    disabled={selected === view.timeZone}
                    onClick={() => void save()}
                >
                    {t('configuration.timeZone.save')}
                </Button>
            </Space>
            <Paragraph type="secondary" style={{ marginTop: 12 }}>
                {t('configuration.timeZone.defaultHint', { timeZone: view.defaultTimeZone })}
            </Paragraph>
            <Alert type="info" showIcon message={t('configuration.timeZone.displayNote')} />
        </div>
    );
};

export default BusinessTimeZoneSettings;
